/*
 * Uni-Varianten-Filter (Form x Größe x Farbe)
 *
 * Baut die Matrix aus dem JSON-Feed /collections/<handle>?view=uni-matrix auf.
 * Ableitung aus der SKU: 5 Ziffern Farbcode + 4 Zeichen Suffix (z.B. 13006T012).
 *
 * Regelwerk:
 *   Form   - alle Formen immer sichtbar, ausgegraut wenn es sie in der aktuellen Farbe nicht gibt
 *   Größe  - alle Größen immer sichtbar, ausgegraut wenn (Form, Größe, Farbe) fehlt oder nicht lieferbar
 *   Farbe  - nur Farben, die es in der aktuellen Form/Größe gibt; nicht lieferbare bleiben sichtbar, aber gesperrt
 *
 * Beim Formwechsel bleibt die Farbe erhalten und es wird die nächstgelegene
 * verfügbare Größe gewählt, damit die Auswahl nicht willkürlich springt.
 */
(function () {
  'use strict';

  var SKU_RE = /^(\d{5})([A-Z]\d{3})$/;
  // Shopify cached Collection-Seiten pro URL, jede Feed-Seite mit eigener
  // Laufzeit. Kommt Seite 1 frisch und Seite 2 noch aus dem alten Stand, geht
  // an der Seitengrenze ein Artikel verloren. Deshalb bekommen alle Seiten
  // eines Ladevorgangs dasselbe Zeitfenster-Token (5-Minuten-Bucket): gleiche
  // URL-Generation, gleicher Render-Stand. Der eigene Cache läuft im selben
  // Takt, damit neue Artikel nach spätestens ~5 Minuten überall auftauchen.
  var FEED_BUCKET = 5 * 60 * 1000;
  var CACHE_TTL = FEED_BUCKET;
  var MAX_PAGES = 20;

  function escAttr(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function UniVariants() {
    return Reflect.construct(HTMLElement, [], UniVariants);
  }

  UniVariants.prototype = Object.create(HTMLElement.prototype);
  UniVariants.prototype.constructor = UniVariants;
  Object.setPrototypeOf(UniVariants, HTMLElement);

  UniVariants.prototype.connectedCallback = function () {
    if (this._booted) return;
    this._booted = true;

    var node = this.dataset.config ? document.querySelector(this.dataset.config) : null;
    if (!node) return;

    try {
      this.config = JSON.parse(node.textContent);
    } catch (e) {
      return;
    }

    this.current = this.parseSku(this.config.sku);
    if (!this.current) return;

    var self = this;
    this.loadItems()
      .then(function (items) {
        self.buildMatrix(items);
        self.render();
      })
      .catch(function (err) {
        if (window.console) console.warn('[uni-variants]', err);
      });
  };

  /* ---------- Daten ---------- */

  UniVariants.prototype.parseSku = function (sku) {
    var match = SKU_RE.exec(String(sku == null ? '' : sku).trim().toUpperCase());
    if (!match) return null;
    var formSize = this.config.suffixes[match[2]];
    if (!formSize) return null;
    return { code: match[1], form: formSize[0], size: String(formSize[1]) };
  };

  UniVariants.prototype.cacheKey = function () {
    // Theme-ID mit in den Key, damit Vorschau und Live sich nicht gegenseitig
    // den Cache vergiften.
    var previewId = new URLSearchParams(window.location.search).get('preview_theme_id') || 'live';
    return 'uni-matrix:' + previewId + '|' + this.config.root + '|' + this.config.collection;
  };

  UniVariants.prototype.loadItems = function () {
    var self = this;
    var bucket = Math.floor(Date.now() / FEED_BUCKET);

    try {
      var raw = sessionStorage.getItem(this.cacheKey());
      if (raw) {
        var cached = JSON.parse(raw);
        if (cached.bucket === bucket && cached.items && cached.items.length) {
          return Promise.resolve(cached.items);
        }
      }
    } catch (e) {
      /* sessionStorage nicht verfügbar - dann eben ohne Cache */
    }

    var root = String(this.config.root || '/').replace(/\/$/, '');
    var plain = root + '/collections/' + encodeURIComponent(this.config.collection) + '?view=uni-matrix';

    // In der Theme-Vorschau kann preview_theme_id nötig sein - kann aber je nach
    // Session auch eine HTML-Bootstrap-Seite auslösen. Deshalb beide Varianten
    // der Reihe nach versuchen statt sich auf eine festzulegen.
    var bases = [];
    var previewId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewId) bases.push(plain + '&preview_theme_id=' + encodeURIComponent(previewId));
    bases.push(plain);

    function fetchPage(base, page, token) {
      var url = base + '&v=' + encodeURIComponent(token) + '&page=' + page;
      // Kein Accept-Header: mit "application/json" beantwortet Shopify die
      // Storefront-URL mit einer eigenen JSON-Struktur statt mit unserem Template.
      return fetch(url, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status + ' bei ' + url);
          return response.text();
        })
        .then(function (text) {
          var body = text.trim();
          var data;
          try {
            data = JSON.parse(body);
          } catch (e) {
            throw new Error('kein JSON bei ' + url + ' -> ' + body.slice(0, 90));
          }
          if (!data || !data.items) {
            throw new Error('Feld "items" fehlt bei ' + url + ' -> ' + body.slice(0, 90));
          }
          return data;
        });
    }

    // Seite 1 liefert die Gesamtseitenzahl, der Rest wird parallel nachgeladen.
    // Einzelne fehlgeschlagene Seiten werfen nicht den ganzen Ladevorgang weg.
    function loadWith(index, token) {
      if (index >= bases.length) return Promise.reject(new Error('Feed über keine der URLs erreichbar'));
      var base = bases[index];

      return fetchPage(base, 1, token)
        .then(function (first) {
          var pages = Math.min(first.pages || 1, MAX_PAGES);
          var rest = [];
          for (var page = 2; page <= pages; page++) {
            rest.push(
              fetchPage(base, page, token).catch(function (err) {
                if (window.console) console.warn('[uni-variants] Seite übersprungen:', err.message);
                return { items: [] };
              })
            );
          }
          return Promise.all(rest).then(function (others) {
            var chunks = [first].concat(others);
            var items = chunks.reduce(function (all, chunk) {
              return all.concat(chunk.items || []);
            }, []);
            var skipped = chunks.reduce(function (sum, chunk) {
              return sum + (chunk.skipped || 0);
            }, 0);
            return { items: dedupe(items), total: first.total || 0, skipped: skipped };
          });
        })
        .catch(function (err) {
          if (window.console) console.warn('[uni-variants]', err.message, '- nächste URL wird versucht');
          return loadWith(index + 1, token);
        });
    }

    // Gleiche SKU aus zwei Seiten (Seitengrenze hat sich verschoben) nur einmal
    // übernehmen, der spätere Eintrag gewinnt nicht über den ersten.
    function dedupe(items) {
      var seen = {};
      var out = [];
      (items || []).forEach(function (item) {
        var sku = item && item.s;
        if (!sku || seen[sku]) return;
        seen[sku] = true;
        out.push(item);
      });
      return out;
    }

    // Notnagel, wenn der Feed gar nicht erreichbar ist (Template fehlt, Redirect
    // auf HTML). Deutlich größere Antwort, deshalb wirklich nur als Fallback.
    function loadProductsJson() {
      var url = root + '/collections/' + encodeURIComponent(self.config.collection) + '/products.json?limit=250';
      function page(number) {
        return fetch(url + '&page=' + number, { credentials: 'same-origin' })
          .then(function (response) {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.json();
          })
          .then(function (data) {
            return (data && data.products) || [];
          });
      }
      return Promise.all([page(1), page(2)]).then(function (chunks) {
        var products = chunks[0].concat(chunks[1]);
        if (!products.length) throw new Error('products.json leer');
        return dedupe(
          products.map(function (product) {
            var variant = (product.variants || [])[0] || {};
            var image = (product.images || [])[0] || {};
            return {
              s: variant.sku,
              h: product.handle,
              t: product.title,
              a: variant.available !== false,
              i: image.src ? image.src.replace(/(\.[a-z]+)(\?|$)/i, '_144x$1$2') : ''
            };
          })
        );
      });
    }

    return loadWith(0, String(bucket))
      .then(function (result) {
        // Gemeldete Gesamtzahl gegen die tatsächlich geladene halten. Weichen
        // sie ab, stammten die Seiten aus unterschiedlichen Cache-Ständen -
        // dann einmal mit frischem Token nachladen, der den Page-Cache umgeht.
        var loaded = result.items.length + (result.skipped || 0);
        if (!result.total || loaded >= result.total) return result.items;

        if (window.console) {
          console.warn(
            '[uni-variants] Feed unvollständig: ' + loaded + ' von ' + result.total +
            ' - wird mit frischem Token neu geladen'
          );
        }

        return loadWith(0, 'r' + Date.now())
          .then(function (retry) {
            // Beide Durchläufe zusammenführen: was in einem Stand fehlte, ist im
            // anderen meist drin. Doppelte fallen im dedupe raus.
            return dedupe(retry.items.concat(result.items));
          })
          .catch(function () {
            return result.items;
          });
      })
      .catch(function (err) {
        if (window.console) console.warn('[uni-variants]', err.message, '- Fallback auf products.json');
        return loadProductsJson();
      })
      .then(function (items) {
        try {
          sessionStorage.setItem(self.cacheKey(), JSON.stringify({ t: Date.now(), bucket: bucket, items: items }));
        } catch (e) {
          /* Quota voll - egal */
        }
        return items;
      });
  };

  UniVariants.prototype.buildMatrix = function (rawItems) {
    var self = this;
    this.items = [];
    this.byKey = {};

    (rawItems || []).forEach(function (raw) {
      var parsed = self.parseSku(raw.s);
      if (!parsed) return;

      var segments = String(raw.t || '')
        .split(',')
        .map(function (part) {
          return part.trim();
        });
      var colorName =
        segments.length >= 3 ? segments.slice(1, segments.length - 1).join(', ') : segments[1] || parsed.code;

      var entry = {
        code: parsed.code,
        form: parsed.form,
        size: parsed.size,
        name: colorName,
        handle: raw.h,
        image: raw.i || '',
        available: !!raw.a
      };

      self.items.push(entry);
      self.byKey[self.key(entry.form, entry.size, entry.code)] = entry;
    });

    // Diagnose: unterscheidet "Feed leer" von "SKUs passen nicht zur Suffix-Tabelle".
    if (this.items.length < 2 && window.console) {
      var sample = (rawItems || []).slice(0, 3).map(function (r) { return r && r.s; });
      console.warn(
        '[uni-variants] Matrix leer. Geladene Einträge: ' + (rawItems || []).length +
        ', davon mit gültiger SKU: ' + this.items.length +
        '. Erste SKUs: ' + JSON.stringify(sample) +
        '. Bekannte Suffixe: ' + Object.keys(this.config.suffixes || {}).join(', ')
      );
    }

    // Das aktuell geöffnete Produkt absichern, falls es (noch) nicht in der Collection hängt.
    var currentKey = this.key(this.current.form, this.current.size, this.current.code);
    if (!this.byKey[currentKey]) {
      var cur = this.config.current || {};
      var curSegments = String(cur.title || '')
        .split(',')
        .map(function (part) {
          return part.trim();
        });
      var entry = {
        code: this.current.code,
        form: this.current.form,
        size: this.current.size,
        name: curSegments.length >= 3 ? curSegments.slice(1, curSegments.length - 1).join(', ') : this.current.code,
        handle: cur.handle,
        image: cur.image || '',
        available: cur.available !== false
      };
      this.items.push(entry);
      this.byKey[currentKey] = entry;
    }
  };

  UniVariants.prototype.key = function (form, size, code) {
    return form + '|' + size + '|' + code;
  };

  UniVariants.prototype.get = function (form, size, code) {
    return this.byKey[this.key(form, size, code)] || null;
  };

  /* ---------- Abfragen ---------- */

  UniVariants.prototype.availableSizes = function (form, code) {
    var self = this;
    return this.config.sizes.filter(function (size) {
      var entry = self.get(form, size.value, code);
      return entry && entry.available;
    });
  };

  UniVariants.prototype.colorsFor = function (form, size) {
    var seen = {};
    var out = [];
    this.items.forEach(function (entry) {
      if (entry.form !== form || entry.size !== size) return;
      if (seen[entry.code]) return;
      seen[entry.code] = true;
      out.push(entry);
    });
    out.sort(function (a, b) {
      return a.name.localeCompare(b.name, 'de');
    });
    return out;
  };

  // Formwechsel: Farbe halten, Größe so nah wie möglich an der aktuellen.
  UniVariants.prototype.targetForForm = function (form) {
    var sizes = this.availableSizes(form, this.current.code);
    if (!sizes.length) return null;

    var wanted = parseFloat(this.current.size);
    var best = sizes[0];
    var bestDistance = Infinity;

    sizes.forEach(function (size) {
      var distance = Math.abs(parseFloat(size.value) - wanted);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = size;
      }
    });

    return this.get(form, best.value, this.current.code);
  };

  /* ---------- Rendering ---------- */

  UniVariants.prototype.url = function (handle) {
    return String(this.config.root || '/').replace(/\/$/, '') + '/products/' + handle;
  };

  UniVariants.prototype.swatchItem = function (options) {
    var id = options.id;
    var inputAttrs = 'type="radio" class="sr-only" id="' + id + '" tabindex="-1"';
    if (options.selected) inputAttrs += ' checked';
    if (options.disabled) inputAttrs += ' disabled';

    var labelClass = options.labelClass + (options.selected || options.disabled ? '' : ' cursor-pointer');
    var style = options.style ? ' style="' + options.style + '"' : '';
    var title = ' title="' + escAttr(options.title) + '"';

    var inner =
      '<input ' + inputAttrs + '>' +
      '<label for="' + id + '" class="' + labelClass + ' pointer-events-none"' + title + style + '>' +
      (options.html || '') +
      '</label>';

    var wrapper =
      options.href && !options.selected && !options.disabled
        ? '<a href="' + escAttr(options.href) + '" class="block">' + inner + '</a>'
        : '<div class="block">' + inner + '</div>';

    return '<li>' + wrapper + '</li>';
  };

  UniVariants.prototype.group = function (legend, extraUlClass, itemsHtml) {
    return (
      '<div class="variant-picker grid gap-5">' +
      '<fieldset class="product-form__input variant-input-wrapper">' +
      '<legend class="sr-only">' +
      escAttr(legend) +
      '</legend>' +
      '<div class="form__label flex items-center justify-between gap-2 w-full">' +
      '<div class="flex gap-2">' +
      escAttr(legend) +
      '</div></div>' +
      '<ul class="swatches swatches--' +
      escAttr(this.config.swatchStyle) +
      extraUlClass +
      ' flex items-start flex-wrap gap-4">' +
      itemsHtml +
      '</ul>' +
      '</fieldset>' +
      '</div>'
    );
  };

  UniVariants.prototype.render = function () {
    var self = this;
    var config = this.config;
    var current = this.current;
    var uid = config.uid;
    var unavailable = config.labels.unavailable;

    /* Formen */
    var shapes = config.forms
      .map(function (form, index) {
        var target = self.targetForForm(form.value);
        var selected = form.value === current.form;
        var disabled = !selected && !target;

        return self.swatchItem({
          id: 'UniShape-' + uid + '-' + index,
          selected: selected,
          disabled: disabled,
          href: target ? self.url(target.handle) : null,
          title: disabled ? form.label + ' - ' + unavailable : form.label,
          labelClass: 'color-swatch with-image relative',
          style: '--swatch-size: var(--sp-10);--swatch-background-image: url(' + escAttr(form.icon) + ');',
          html:
            '<span class="tooltip opacity-0 pointer-events-none text-xs rounded-full z-10">' +
            escAttr(disabled ? form.label + ' - ' + unavailable : form.label) +
            '</span>'
        });
      })
      .join('');

    /* Größen */
    var sizeList = config.sizes;
    if (config.sizeVisibility === 'per-form') {
      // Größen, die es bei dieser Form grundsätzlich nicht gibt, ganz ausblenden
      // statt sie durchgestrichen anzuzeigen (Spitze hätte sonst 5 tote Buttons).
      sizeList = config.sizes.filter(function (size) {
        return self.items.some(function (entry) {
          return entry.form === current.form && entry.size === size.value;
        });
      });
    }

    var sizes = sizeList
      .map(function (size, index) {
        var entry = self.get(current.form, size.value, current.code);
        var selected = size.value === current.size;
        var disabled = !selected && !(entry && entry.available);

        return self.swatchItem({
          id: 'UniSize-' + uid + '-' + index,
          selected: selected,
          disabled: disabled,
          href: entry ? self.url(entry.handle) : null,
          title: disabled ? size.label + ' - ' + unavailable : size.label,
          labelClass: 'label-swatch text-sm font-medium leading-none relative',
          html: escAttr(size.label)
        });
      })
      .join('');

    /* Farben */
    var colorList = this.colorsFor(current.form, current.size);
    var colors = colorList
      .map(function (entry, index) {
        var selected = entry.code === current.code;
        var disabled = !selected && !entry.available;
        var style = entry.image ? '--swatch-background-image: url(' + escAttr(entry.image) + ');' : '';

        return self.swatchItem({
          id: 'UniColor-' + uid + '-' + index,
          selected: selected,
          disabled: disabled,
          href: self.url(entry.handle),
          title: disabled ? entry.name + ' - ' + unavailable : entry.name,
          labelClass:
            'color-swatch relative' +
            (entry.image ? ' with-image' : '') +
            (config.colorSwatchClass ? ' ' + config.colorSwatchClass : ''),
          style: style,
          html:
            '<span class="tooltip opacity-0 pointer-events-none text-xs rounded-full z-10">' +
            escAttr(entry.name) +
            '</span>'
        });
      })
      .join('');

    this.innerHTML =
      this.group(config.labels.shape, '', shapes) +
      this.group(config.labels.size, '', sizes) +
      this.group(config.labels.color, ' swatches--variant', colors);
  };

  if (!window.customElements.get('uni-variants')) {
    window.customElements.define('uni-variants', UniVariants);
  }
})();